import { useState, useEffect, useContext, createContext } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  signInWithRedirect,
  getRedirectResult
} from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Safety timeout to prevent infinite loading
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Safety timeout: Setting loading to false after 15 seconds');
        setLoading(false);
      }
    }, 15000);

    return () => clearTimeout(safetyTimeout);
  }, [loading]);

  // Call Cloud Function to determine user role with retry logic (HTTP approach)
  const determineUserRole = async (user, maxRetries = 3) => {
    if (!user || !user.email) {
      return 'DENIED';
    }

    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        process.env.REACT_APP_ENVIRONMENT === 'development' && console.log(`🔐 Authenticating user: ${user.email} (attempt ${attempt}/${maxRetries})`);
        
        // Get the user's ID token
        const idToken = await auth.currentUser.getIdToken();
        
        // Make direct HTTP request to avoid CORS issues
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'truck-check-98045';
        const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/setUserRoleHTTP`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({}),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        process.env.REACT_APP_ENVIRONMENT === 'development' && console.log(`✅ Authentication successful: ${user.email} → ${result.role}`);
        
        return result.role || 'DENIED';
      } catch (error) {
        lastError = error;
        if (error.name === 'AbortError') {
          console.error(`⏱️ Authentication attempt ${attempt}/${maxRetries} timed out after 10 seconds`);
        } else {
          console.error(`❌ Authentication attempt ${attempt}/${maxRetries} failed:`, error.message);
        }
        
        // Wait before retry (exponential backoff: 1s, 2s, 4s)
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed - fail secure
    console.error('🚫 Authentication failed. Access denied.');
    throw new Error(`Authentication failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  };

  // Sign in with Google OAuth with fallback mechanism
  const signInWithGoogle = async (useRedirect = false) => {
    try {
      setError(null);
      setLoading(true);
      
      if (useRedirect) {
        // Use redirect for mobile devices (better UX)
        await signInWithRedirect(auth, googleProvider);
      } else {
        // Use popup for desktop (faster)
        const result = await signInWithPopup(auth, googleProvider);
        process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ Google sign-in successful:', result.user.email);
      }
    } catch (error) {
      console.error('❌ Sign-in error:', error.message);
      
      // Try fallback method if redirect fails
      if (useRedirect && error.code !== 'auth/cancelled-popup-request') {
        try {
          const result = await signInWithPopup(auth, googleProvider);
          process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ Sign-in successful:', result.user.email);
          return;
        } catch (popupError) {
          setError(`Authentication failed: ${popupError.message}`);
        }
      } else {
        setError(`Sign-in failed: ${error.message}`);
      }
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserRole(null);
    } catch (error) {
      console.error('❌ Sign out error:', error);
      setError(error.message);
    }
  };

  // Check for redirect result on app load
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('✅ Google sign-in successful:', result.user.email);
          setError(null); // Clear any existing errors
        }
      } catch (error) {
        console.error('❌ Redirect error:', error.message);
        setError(`Redirect error: ${error.message}`);
      }
    };

    checkRedirectResult();
  }, []);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      process.env.REACT_APP_ENVIRONMENT === 'development' && console.log('🔄 Auth state changed:', user ? user.email : 'No user');
      setUser(user);
      
      if (user) {
        try {
          setError('Determining access permissions...'); // Show loading message
          const role = await determineUserRole(user);
          setUserRole(role);
          setError(null); // Clear errors on successful auth
        } catch (error) {
          console.error('❌ Role determination failed:', error.message);
          setUserRole('DENIED');
          setError(`Access denied: ${error.message}. Please contact your system administrator if this persists.`);
        } finally {
          // Always clear loading state, even if role determination fails
          setLoading(false);
        }
      } else {
        setUserRole(null);
        setError(null); // Clear errors when user logs out
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Context value
  const value = {
    user,
    userRole,
    loading,
    error,
    signInWithGoogle,
    signOut,
    // Helper functions
    isAdmin: userRole === 'ADMIN',
    isInspector: userRole === 'INSPECTOR' || userRole === 'ADMIN',
    isSupervisor: userRole === 'SUPERVISOR' || userRole === 'ADMIN',
    hasAccess: userRole && userRole !== 'DENIED'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;